/* ============================================================================
   procedural.ts — Génère un pattern ornemental ASCII déterministe à partir
   de seed + ornamentStyle + symmetry. Pas d'image source.

   Approche : on dessine des courbes paramétriques sur une grille de
   "densité" (chaque cell accumule une intensité), puis on map les
   intensités → glyphs via le set choisi.

   PRNG : mulberry32 (seedable, déterministe, ~10 LOC).
   ============================================================================ */

import { getGlyphSet, luminanceToGlyph } from './glyphSets';
import type { CanvasParams } from '../canvasStore';

export type ProceduralParams = Pick<
  CanvasParams,
  'glyphSet' | 'density' | 'invert' | 'seed' | 'ornamentStyle' | 'symmetry'
>;

/* ----------------------------------------------------------------------------
   PRNG mulberry32 — seedable + déterministe
   ---------------------------------------------------------------------------- */

export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function rand() {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/* ----------------------------------------------------------------------------
   Densité accumulator → glyph grid
   ---------------------------------------------------------------------------- */

/** Crée une grille rows × cols remplie de zéros. */
function emptyDensityGrid(rows: number, cols: number): number[][] {
  const grid: number[][] = new Array(rows);
  for (let r = 0; r < rows; r++) {
    grid[r] = new Array(cols).fill(0);
  }
  return grid;
}

/**
 * Stamp une intensité gaussienne autour d'un point (x, y) en coords grille
 * (peuvent être fractionnaires). Radius en cells.
 */
function stamp(
  grid: number[][],
  x: number,
  y: number,
  radius: number,
  intensity: number,
): void {
  const rows = grid.length;
  const cols = grid[0]?.length ?? 0;
  const r2 = radius * radius;
  const x0 = Math.max(0, Math.floor(x - radius));
  const x1 = Math.min(cols - 1, Math.ceil(x + radius));
  const y0 = Math.max(0, Math.floor(y - radius));
  const y1 = Math.min(rows - 1, Math.ceil(y + radius));
  for (let yy = y0; yy <= y1; yy++) {
    for (let xx = x0; xx <= x1; xx++) {
      const dx = xx - x;
      const dy = yy - y;
      const d2 = dx * dx + dy * dy;
      if (d2 <= r2) {
        const fall = 1 - d2 / r2; // 0..1
        grid[yy]![xx] = (grid[yy]![xx] ?? 0) + intensity * fall;
      }
    }
  }
}

/* ----------------------------------------------------------------------------
   Curve drawers — chaque style trace ses courbes en stampant
   ---------------------------------------------------------------------------- */

/** Spirale logarithmique baroque, ~2-3 spirales centrées. */
function drawBaroqueScroll(
  grid: number[][],
  rand: () => number,
): void {
  const rows = grid.length;
  const cols = grid[0]?.length ?? 0;
  const cx = cols / 2;
  const cy = rows / 2;
  const baseR = Math.min(cols, rows) * 0.45;
  const spirals = 2 + Math.floor(rand() * 2); // 2..3
  for (let s = 0; s < spirals; s++) {
    const phase = rand() * Math.PI * 2;
    const a = 0.05 + rand() * 0.08;
    const tMax = 8 + rand() * 4;
    const dt = 0.05;
    for (let t = 0; t < tMax; t += dt) {
      const r = baseR * Math.exp(-a * t);
      const angle = t + phase;
      const x = cx + r * Math.cos(angle);
      const y = cy + r * Math.sin(angle);
      stamp(grid, x, y, 1.5, 0.7);
    }
  }
  // ornement central
  stamp(grid, cx, cy, 2.5, 1.0);
}

/** Couronne radiale florale, n pétales sur un cercle. */
function drawFloralWreath(
  grid: number[][],
  rand: () => number,
): void {
  const rows = grid.length;
  const cols = grid[0]?.length ?? 0;
  const cx = cols / 2;
  const cy = rows / 2;
  const R = Math.min(cols, rows) * 0.32;
  const petals = 8 + Math.floor(rand() * 6); // 8..13
  for (let p = 0; p < petals; p++) {
    const a = (p / petals) * Math.PI * 2;
    const px = cx + R * Math.cos(a);
    const py = cy + R * Math.sin(a);
    // pétale = ovale autour du centre
    for (let t = 0; t < Math.PI * 2; t += 0.2) {
      const localR = 2.5 + rand() * 1.5;
      const ex = px + localR * Math.cos(t) * 0.6;
      const ey = py + localR * Math.sin(t);
      stamp(grid, ex, ey, 1.2, 0.55);
    }
  }
  // anneau interne discret
  for (let t = 0; t < Math.PI * 2; t += 0.05) {
    const x = cx + R * 0.55 * Math.cos(t);
    const y = cy + R * 0.55 * Math.sin(t);
    stamp(grid, x, y, 0.8, 0.3);
  }
}

/** Bordure en sinusoïde + ramifications. */
function drawVineBorder(
  grid: number[][],
  rand: () => number,
): void {
  const rows = grid.length;
  const cols = grid[0]?.length ?? 0;
  const amp = rows * 0.22;
  const cy = rows / 2;
  const phase = rand() * Math.PI * 2;
  const freq = 0.05 + rand() * 0.04;
  for (let x = 0; x < cols; x += 0.5) {
    const y = cy + amp * Math.sin(x * freq + phase);
    stamp(grid, x, y, 1.0, 0.6);
    // ramification
    if ((x | 0) % 6 === 0) {
      const branchLen = 3 + rand() * 4;
      const dir = Math.cos(x * freq + phase) > 0 ? -1 : 1;
      for (let b = 0; b < branchLen; b += 0.5) {
        stamp(grid, x + b * 0.4, y + dir * b, 0.8, 0.4);
      }
    }
  }
}

/** Courbes en S miroir (arabesque). */
function drawArabesqueMirror(
  grid: number[][],
  rand: () => number,
): void {
  const rows = grid.length;
  const cols = grid[0]?.length ?? 0;
  const cx = cols / 2;
  const cy = rows / 2;
  const A = Math.min(cols, rows) * 0.35;
  const phase = rand() * Math.PI;
  // une courbe en S paramétrique
  for (let t = -Math.PI; t <= Math.PI; t += 0.04) {
    const x = cx + A * Math.sin(t * 2 + phase);
    const y = cy + A * 0.6 * Math.sin(t);
    stamp(grid, x, y, 1.2, 0.6);
  }
  // deuxième courbe contre-balancée
  for (let t = -Math.PI; t <= Math.PI; t += 0.04) {
    const x = cx + A * Math.sin(-t * 2 + phase);
    const y = cy + A * 0.6 * Math.sin(t + 0.5);
    stamp(grid, x, y, 1.0, 0.45);
  }
}

/* ----------------------------------------------------------------------------
   Symmetry post-processing
   ---------------------------------------------------------------------------- */

function applySymmetry(grid: number[][], sym: ProceduralParams['symmetry']): void {
  const rows = grid.length;
  if (rows === 0) return;
  const cols = grid[0]!.length;
  if (sym === 'horizontal') {
    // mirror gauche → droite
    for (let r = 0; r < rows; r++) {
      const row = grid[r]!;
      for (let c = 0; c < Math.floor(cols / 2); c++) {
        const v = Math.max(row[c]!, row[cols - 1 - c]!);
        row[c] = v;
        row[cols - 1 - c] = v;
      }
    }
  } else if (sym === 'vertical') {
    for (let r = 0; r < Math.floor(rows / 2); r++) {
      const top = grid[r]!;
      const bot = grid[rows - 1 - r]!;
      for (let c = 0; c < cols; c++) {
        const v = Math.max(top[c]!, bot[c]!);
        top[c] = v;
        bot[c] = v;
      }
    }
  }
  // 'asymmetric' → no-op
}

/* ----------------------------------------------------------------------------
   Pipeline procedural
   ---------------------------------------------------------------------------- */

export function generateProceduralAscii(params: ProceduralParams): string[][] {
  const cols = Math.max(16, Math.min(400, Math.round(params.density)));
  const rows = Math.max(8, Math.round(cols * 0.55));
  const grid = emptyDensityGrid(rows, cols);
  const rand = mulberry32(params.seed | 0);

  switch (params.ornamentStyle) {
    case 'baroque-scroll':
      drawBaroqueScroll(grid, rand);
      break;
    case 'floral-wreath':
      drawFloralWreath(grid, rand);
      break;
    case 'vine-border':
      drawVineBorder(grid, rand);
      break;
    case 'arabesque-mirror':
      drawArabesqueMirror(grid, rand);
      break;
  }
  applySymmetry(grid, params.symmetry);

  // Normaliser densité → [0..1]
  let maxV = 0;
  for (let r = 0; r < rows; r++) {
    const row = grid[r]!;
    for (let c = 0; c < cols; c++) {
      if (row[c]! > maxV) maxV = row[c]!;
    }
  }
  const set = getGlyphSet(params.glyphSet, params.invert);
  const out: string[][] = [];
  for (let r = 0; r < rows; r++) {
    const row = grid[r]!;
    const outRow: string[] = new Array(cols);
    for (let c = 0; c < cols; c++) {
      const norm = maxV > 0 ? row[c]! / maxV : 0;
      outRow[c] = luminanceToGlyph(norm, set);
    }
    out.push(outRow);
  }
  return out;
}
